import type { Project, ProjectCategory } from '@/types'
import { QUOTE_PATH } from '@/constants/site'

export const projectCategories: ProjectCategory[] = ['New door', 'Repair', 'Opener', 'Commercial']

export const projects: Project[] = [
    {
        slug: 'fairhaven-double-door',
        title: "Fairhaven double door",
        category: 'New door',
        location: "Fairhaven",
        areaSlug: 'fairhaven',
        summary: "16×7 · Insulated charcoal flush panel · 5 h on site",
        description: "Out went a dented steel door from 1998. In went an insulated charcoal flush-panel door, paired with a quiet belt-drive opener.",
        caption: "Fairhaven, 16×7 double door. The dented 1998 steel door made way for an insulated charcoal flush-panel door and a quiet belt-drive opener, all fitted in 5 hours.",
        details: [
            { label: "Location", value: "Fairhaven" },
            { label: "Door size", value: "16×7 double" },
            { label: "Product", value: "Norrland Flush, Charcoal, R-16" },
            { label: "Time on site", value: "5 hours" }
        ],
        quote: {
            text: "The old door rattled the whole kitchen. Now I can't hear it open.",
            author: "Homeowner, Fairhaven"
        },
        cover: '/images/projects/fairhaven-cover.jpg',
        before: '/images/doors/colors/norrland-flush-white.jpg',
        after: '/images/doors/colors/norrland-flush-black.jpg',
        alt: "Fairhaven double garage door"
    },
    {
        slug: 'ashford-park-carriage-door',
        title: "Ashford Park carriage door",
        category: 'New door',
        location: "Ashford Park",
        areaSlug: 'ashford-park',
        summary: "16×7 · Walnut wood-look composite · 6 h on site",
        description: "A walnut wood-look composite carriage-house door that gave this Ashford Park home a warmer, more crafted front.",
        details: [
            { label: "Location", value: "Ashford Park" },
            { label: "Door size", value: "16×7 double" },
            { label: "Product", value: "Walnut wood-look composite carriage door" },
            { label: "Time on site", value: "6 hours" },
            { label: "Completed", value: "Sep 10, 2026" }
        ],
        cover: '/images/projects/ashford-park-cover.jpg',
        before: '/images/projects/ashford-park-before.jpg',
        after: '/images/projects/ashford-park-after.jpg',
        alt: "Ashford Park carriage-house garage door"
    },
    {
        slug: 'lakeview-full-view-glass',
        title: "Lakeview full-view glass",
        category: 'New door',
        location: "Lakeview Heights",
        areaSlug: 'lakeview-heights',
        summary: "9×7 · Black aluminum, frosted glass · 4 h on site",
        description: "Black aluminum frames and frosted glass bring daylight into this Lakeview Heights garage while keeping it private.",
        details: [
            { label: "Location", value: "Lakeview Heights" },
            { label: "Door size", value: "9×7 single" },
            { label: "Product", value: "Black aluminum full-view door, frosted glass" },
            { label: "Time on site", value: "4 hours" },
            { label: "Completed", value: "Aug 26, 2026" }
        ],
        cover: '/images/projects/lakeview-cover.jpg',
        before: '/images/projects/lakeview-before.jpg',
        after: '/images/projects/lakeview-after.jpg',
        alt: "Black aluminum full-view garage door in Lakeview Heights"
    },
    {
        slug: 'millbrook-spring-replacement',
        title: "Millbrook spring replacement",
        category: 'Repair',
        location: "Millbrook",
        areaSlug: 'millbrook',
        summary: "Matched torsion pair, new cables · 48 min",
        description: "Both torsion springs replaced as a matched pair, with new lift cables fitted on the same visit, in 48 minutes flat.",
        details: [
            { label: "Location", value: "Millbrook" },
            { label: "Work", value: "Matched torsion spring pair, new cables" },
            { label: "Time on site", value: "48 minutes" },
            { label: "Completed", value: "Aug 14, 2026" }
        ],
        cover: '/images/projects/millbrook-cover.jpg',
        before: '/images/projects/millbrook-before.jpg',
        after: '/images/projects/millbrook-after.jpg',
        alt: "Torsion spring replacement in Millbrook"
    },
    {
        slug: 'pine-ridge-smart-opener',
        title: "Pine Ridge smart opener",
        category: 'Opener',
        location: "Pine Ridge",
        areaSlug: 'pine-ridge',
        summary: "Belt drive, Wi-Fi, battery backup · 1 h 40 min",
        description: "A belt-drive smart opener with Wi-Fi and battery backup, so this Pine Ridge door now runs from a phone and keeps working through a power cut.",
        details: [
            { label: "Location", value: "Pine Ridge" },
            { label: "Work", value: "Belt-drive opener, Wi-Fi, battery backup" },
            { label: "Time on site", value: "1 h 40 min" },
            { label: "Completed", value: "Jul 29, 2026" }
        ],
        cover: '/images/projects/pine-ridge-cover.jpg',
        before: '/images/projects/pine-ridge-before.jpg',
        after: '/images/projects/pine-ridge-after.jpg',
        alt: "Belt-drive smart opener installed in Pine Ridge"
    },
    {
        slug: 'stonebridge-workshop-doors',
        title: "Stonebridge workshop doors",
        category: 'Commercial',
        location: "Stonebridge",
        areaSlug: 'stonebridge',
        summary: "Two rolling steel doors, new operators · 1 day",
        description: "Two rolling steel doors and their new operators, fitted for a Stonebridge workshop in a single day.",
        details: [
            { label: "Location", value: "Stonebridge" },
            { label: "Work", value: "Two rolling steel doors, new operators" },
            { label: "Time on site", value: "1 day" },
            { label: "Completed", value: "Jul 16, 2026" }
        ],
        cover: '/images/projects/stonebridge-cover.jpg',
        before: '/images/projects/stonebridge-before.jpg',
        after: '/images/projects/stonebridge-after.jpg',
        alt: "Rolling steel workshop doors in Stonebridge"
    }
]

export const featuredProject: Project = projects[0]

export const getProjectBySlug = (slug: string): Project | undefined =>
    projects.find(project => project.slug === slug)

export const projectCta: Record<ProjectCategory, { title: string, label: string, href: string }> = {
    'New door': {
        title: "Picture a door like this on your home?",
        label: "Help me choose",
        href: `${QUOTE_PATH}?service=new-doors&entry=help_me_choose`
    },
    Repair: {
        title: "Seeing the same trouble at home?",
        label: "Get my written price",
        href: `${QUOTE_PATH}?service=broken-spring-repair&entry=project`
    },
    Opener: {
        title: "Want an opener like this one?",
        label: "Get my written price",
        href: `${QUOTE_PATH}?service=openers-smart-garage&entry=project`
    },
    Commercial: {
        title: "Is a door holding up your business?",
        label: "Book a site visit",
        href: `${QUOTE_PATH}?service=commercial-doors&entry=project`
    }
}
